import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  TrendingDown,
  Clock,
  DollarSign,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Zap,
  ChevronDown,
  BarChart3,
  Scale,
  Users,
  MessageCircle,
  FileText,
  Calendar,
  AlertCircle,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import RequestDemoModal from "@/components/modals/RequestDemoModal";

export default function CalculadoraAhorroLanding() {
  const [procesos, setProcesos] = useState<number>(180);
  const [minutosPorProceso, setMinutosPorProceso] = useState<number>(2.0);
  const [costoHora, setCostoHora] = useState<number>(15000);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const COSTO_MIA_BASE = 179000;
  const DIAS_HABILES = 22;
  const COSTO_TRADICIONAL_UNITARIO = 3500;

  // Calculos en tiempo real
  const stats = useMemo(() => {
    const minutosDiarios = procesos * minutosPorProceso;
    const horasDiarias = minutosDiarios / 60;
    const horasMensuales = horasDiarias * DIAS_HABILES;
    const costoManualMensual = horasMensuales * costoHora;

    const ahorroMensual = Math.max(0, costoManualMensual - COSTO_MIA_BASE);
    const ahorroAnual = ahorroMensual * 12;
    const porcentajeAhorro = costoManualMensual > 0 
      ? Math.round((ahorroMensual / costoManualMensual) * 100) 
      : 0;

    const costoUnitarioMiA = procesos > 0 ? COSTO_MIA_BASE / procesos : 0;
    const costoTradicional = procesos * COSTO_TRADICIONAL_UNITARIO;

    return {
      horasDiarias: horasDiarias.toFixed(1),
      horasMensuales: Math.round(horasMensuales),
      costoManualMensual,
      ahorroMensual,
      ahorroAnual,
      porcentajeAhorro,
      costoUnitarioMiA: Math.round(costoUnitarioMiA),
      costoTradicional,
    };
  }, [procesos, minutosPorProceso, costoHora]);

  const formatCOP = (num: number) => {
    return "$" + Math.round(num).toLocaleString("es-CO");
  };

  const whatsappMessage = encodeURIComponent(
    `Hola Nudier, usé la calculadora de MiA para ${procesos} procesos judiciales en mi despacho. ` +
    `Calculé un ahorro de ${formatCOP(stats.ahorroMensual)} COP/mes. Me gustaría agendar la demo de 15 minutos para ver la vigilancia en vivo.`
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <SEO
        title="Calculadora de Ahorro Judicial | MiA — Hasta 400 Procesos por $179.000/mes"
        description="Calcula en tiempo real cuánto tiempo y dinero ahorra tu despacho jurídico automatizando la vigilancia de procesos en Rama Judicial y SAMAI."
      />
      <Header />

      {/* HERO SECTION */}
      <section className="relative pt-28 pb-16 bg-gradient-to-b from-blue-50/70 via-slate-50 to-slate-50 border-b border-slate-200/80">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100/80 border border-blue-200 text-blue-800 text-xs sm:text-sm font-semibold mb-6 shadow-sm">
            <span className="h-2 w-2 rounded-full bg-blue-600 animate-pulse" />
            Infraestructura de Productividad para Litigantes en Colombia
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-6 leading-tight">
            ¿Todavía revisas tus procesos judiciales{" "}
            <span className="bg-gradient-to-r from-blue-700 via-indigo-600 to-blue-800 bg-clip-text text-transparent">
              uno por uno?
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-8 font-normal leading-relaxed">
            Automatiza la vigilancia de hasta <strong className="text-blue-700 font-bold">400 procesos por $179.000 COP al mes</strong>. 
            Descubre en segundos cuántas horas y dinero pierde tu despacho en revisiones manuales de estrado.
          </p>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-slate-600 font-medium">
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>Rama Judicial + SAMAI</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>Solo ~$447 COP por proceso</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>Alertas y providencias con IA</span>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR INTERACTIVE HUB */}
      <section id="calculadora" className="py-12 bg-slate-50 relative z-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xl shadow-slate-200/60 relative overflow-hidden">
            
            {/* Top Bar Accent */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500" />

            {/* CONTROLS (6 COLS) */}
            <div className="lg:col-span-6 flex flex-col justify-between space-y-8 pr-0 lg:pr-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <Scale className="h-6 w-6 text-blue-600" />
                  Simula la Operación de tu Firma
                </h2>
                <p className="text-sm text-slate-500">
                  Ajusta los controles para calcular el costo y tiempo real de la vigilancia manual en tu despacho:
                </p>
              </div>

              {/* CONTROL 1: PROCESOS */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    Procesos judiciales activos
                  </label>
                  <span className="px-3.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 font-extrabold text-lg shadow-sm">
                    {procesos}
                  </span>
                </div>
                <Slider
                  value={[procesos]}
                  onValueChange={(val) => setProcesos(val[0])}
                  min={20}
                  max={500}
                  step={10}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-slate-400 font-semibold">
                  <span>20 procesos</span>
                  <span>250 procesos</span>
                  <span>500+ procesos</span>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {[50, 180, 300, 400].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setProcesos(num)}
                      className={`text-xs px-3.5 py-1.5 rounded-full border transition-all ${
                        procesos === num
                          ? "bg-blue-600 border-blue-600 text-white font-bold shadow-sm"
                          : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {num} procesos
                    </button>
                  ))}
                </div>
              </div>

              {/* CONTROL 2: TIEMPO POR PROCESO */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    Minutos promedio por proceso
                  </label>
                  <span className="px-3.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 font-bold text-base shadow-sm">
                    {minutosPorProceso.toFixed(1)} min
                  </span>
                </div>
                <Slider
                  value={[minutosPorProceso]}
                  onValueChange={(val) => setMinutosPorProceso(val[0])}
                  min={1.0}
                  max={4.0}
                  step={0.5}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-slate-400 font-semibold">
                  <span>1 min (rápido)</span>
                  <span>2 min (promedio Rama)</span>
                  <span>4 min (con captchas y caídas)</span>
                </div>
              </div>

              {/* CONTROL 3: COSTO HORA */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-blue-600" />
                    Costo por hora del dependiente / asistente
                  </label>
                  <span className="px-3.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 font-bold text-base shadow-sm">
                    {formatCOP(costoHora)} COP
                  </span>
                </div>
                <Slider
                  value={[costoHora]}
                  onValueChange={(val) => setCostoHora(val[0])}
                  min={8000}
                  max={35000}
                  step={1000}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-slate-400 font-semibold">
                  <span>$8.000 (Auxiliar)</span>
                  <span>$15.000 (Dependiente)</span>
                  <span>$35.000 (Abogado Jr)</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200 text-xs text-amber-900 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Dato real de litigio:</strong> En despachos con más de 100 procesos, el equipo pierde entre 2 y 3 horas diarias únicamente verificando estados y providencias que no se han movido.
                </span>
              </div>
            </div>

            {/* RESULTS DASHBOARD (6 COLS) */}
            <div className="lg:col-span-6 bg-slate-900 text-white rounded-2xl p-6 sm:p-8 flex flex-col justify-between space-y-6 shadow-xl shadow-slate-950/20">
              
              <div className="border-b border-slate-800 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-emerald-400" />
                    Diagnóstico Financiero de tu Despacho
                  </h3>
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-950/50 text-xs font-semibold">
                    22 Días Hábiles / Mes
                  </Badge>
                </div>
              </div>

              {/* 4 METRICS GRID */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/80 border border-slate-700/60 p-4 rounded-xl">
                  <span className="text-xs text-slate-400 font-medium block mb-1">Horas dedicadas a clics</span>
                  <div className="text-2xl font-black text-rose-400">
                    {stats.horasMensuales} <span className="text-xs font-semibold">hrs/mes</span>
                  </div>
                  <span className="text-xs text-slate-400">{stats.horasDiarias} horas al día</span>
                </div>

                <div className="bg-slate-800/80 border border-slate-700/60 p-4 rounded-xl">
                  <span className="text-xs text-slate-400 font-medium block mb-1">Costo manual actual</span>
                  <div className="text-2xl font-black text-rose-400">
                    {formatCOP(stats.costoManualMensual)}
                  </div>
                  <span className="text-xs text-slate-400">en horas no facturables</span>
                </div>

                <div className="bg-slate-800/80 border border-slate-700/60 p-4 rounded-xl">
                  <span className="text-xs text-slate-400 font-medium block mb-1">Inversión mensual MiA</span>
                  <div className="text-2xl font-black text-emerald-400">
                    $179.000 <span className="text-xs font-semibold">COP</span>
                  </div>
                  <span className="text-xs text-slate-400">{formatCOP(stats.costoUnitarioMiA)} por proceso</span>
                </div>

                <div className="bg-slate-800/80 border border-slate-700/60 p-4 rounded-xl">
                  <span className="text-xs text-slate-400 font-medium block mb-1">Ahorro anual estimado</span>
                  <div className="text-2xl font-black text-emerald-400">
                    {formatCOP(stats.ahorroAnual)}
                  </div>
                  <span className="text-xs text-slate-400">capital recuperado</span>
                </div>
              </div>

              {/* HERO SAVINGS BOX */}
              <div className="relative p-5 rounded-2xl bg-gradient-to-br from-emerald-950/60 via-slate-800 to-slate-800 border border-emerald-500/40 text-center shadow-lg">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-0.5 rounded-full bg-emerald-500 text-slate-950 text-xs font-extrabold uppercase tracking-wider">
                  Ahorro Neto Mensual
                </div>
                <p className="text-xs text-slate-300 font-medium mt-2">
                  Dinero que dejas de gastar en tareas mecánicas:
                </p>
                <div className="text-3xl sm:text-4xl font-black text-emerald-400 my-1">
                  {formatCOP(stats.ahorroMensual)} <span className="text-sm font-bold text-emerald-300">COP/mes</span>
                </div>
                <p className="text-xs font-semibold text-emerald-300">
                  Ahorras un {stats.porcentajeAhorro}% cada mes + {stats.horasMensuales} horas libres para sustanciar
                </p>
              </div>

              {/* COMPARATIVE BARS */}
              <div className="space-y-3 pt-1">
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span className="text-slate-300">Revisión manual humana (Tus costos actuales)</span>
                    <span className="text-rose-400 font-bold">{formatCOP(stats.costoManualMensual)}</span>
                  </div>
                  <div className="h-2.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-rose-500 rounded-full w-full" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span className="text-slate-300">Plataformas tradicionales ($3.500/proc)</span>
                    <span className="text-amber-400 font-bold">{formatCOP(stats.costoTradicional)}</span>
                  </div>
                  <div className="h-2.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(100, Math.max(15, (stats.costoTradicional / (stats.costoManualMensual || 1)) * 100))}%`
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span className="text-emerald-400 font-bold">MiA Automatización Inteligente (Hasta 400 procesos)</span>
                    <span className="text-emerald-400 font-bold">$179.000</span>
                  </div>
                  <div className="h-2.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded-full transition-all duration-500 shadow-sm shadow-emerald-400"
                      style={{
                        width: `${Math.min(100, Math.max(10, (COSTO_MIA_BASE / (stats.costoManualMensual || 1)) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  onClick={() => setIsDemoModalOpen(true)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold text-base py-6 rounded-xl shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.01]"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Quiero probar MiA con mis procesos
                </Button>

                <a
                  href={`https://api.whatsapp.com/send?phone=573507679981&text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button
                    variant="outline"
                    className="w-full bg-slate-800/80 hover:bg-slate-700/80 border-slate-600 text-emerald-300 font-semibold py-5 rounded-xl transition-all"
                  >
                    <MessageCircle className="h-4 w-4 mr-2 text-emerald-400" />
                    Agendar Demo de 15 Min por WhatsApp
                  </Button>
                </a>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* WHY MIA VALUE PROPOSITION */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4">
              No es otra IA. Es infraestructura de productividad.
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              MiA no viene a redactar teoría genérica; viene a resolver la carpintería pesada del litigio colombiano para que tu equipo sustancie y facture más.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200/90 p-8 rounded-2xl hover:border-blue-400 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-6">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Vigilancia Automatizada 24/7</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Rastreo continuo en Consulta Web y SAMAI. No más captchas caídos ni horas muertas frente a la pantalla. Si hay un auto o fijación en lista, lo sabes al instante.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200/90 p-8 rounded-2xl hover:border-blue-400 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center mb-6">
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Resumen Inteligente de Providencias</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Nuestra IA lee la providencia o auto emitido, extrae el sentido de la decisión, identifica si hay traslados activos y te alerta sobre términos perentorios.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200/90 p-8 rounded-2xl hover:border-blue-400 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-6">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Escala sin Duplicar Personal</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Pasa de 100 a 400 procesos manteniendo la misma nómina. Tu asistente deja de dar clics y pasa a elaborar memoriales y atender clientes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-slate-900 mb-3">Preguntas Frecuentes</h2>
            <p className="text-slate-600 text-sm">Todo lo que necesitas saber antes de automatizar tu despacho con MiA.</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "¿Cómo se cargan mis procesos a MiA?",
                a: "Puedes importar tu lista en bloque copiando y pegando tus números de radicado (de 23 dígitos) o subiendo un archivo Excel. En menos de 2 minutos el robot indexa el historial completo y activa la vigilancia."
              },
              {
                q: "¿Qué incluye la tarifa de $179.000 COP al mes?",
                a: "Incluye la vigilancia activa de hasta 400 procesos judiciales simultáneos en juzgados, tribunales y altas cortes de Colombia, alertas en tiempo real vía correo/plataforma, módulo de consulta y análisis con Inteligencia Artificial."
              },
              {
                q: "¿Reemplaza esto a mi dependiente judicial?",
                a: "No busca reemplazarlo, sino potenciarlo. Le quita 3 horas diarias de trabajo mecánico e improductivo de revisión de estrados para que pueda dedicarse a sustanciar, redactar y apoyar casos complejos."
              },
              {
                q: "¿Tengo permanencia o contratos forzosos?",
                a: "Cero permanencia. Es una suscripción mensual en la que pagas mes a mes. Puedes cancelar o pausar en cualquier momento sin penalidades ni amarres."
              }
            ].map((faq, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm transition-all"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 text-left font-bold text-slate-800 flex justify-between items-center hover:text-blue-600 transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-slate-400 transition-transform ${
                      openFaq === idx ? "rotate-180 text-blue-600" : ""
                    }`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white text-center">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
            Prueba MiA con tus propios radicados
          </h2>
          <p className="text-blue-100 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Agenda una sesión de 15 minutos. Cargamos 3 de tus procesos en vivo y te mostramos cómo funciona la vigilancia antes de tomar cualquier decisión.
          </p>
          <Button
            onClick={() => setIsDemoModalOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold text-base px-8 py-6 rounded-xl shadow-xl shadow-emerald-950/40 transition-all hover:scale-105"
          >
            Solicitar Demostración de 15 Minutos
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />

      {/* REQUEST DEMO MODAL INTEGRATION */}
      <RequestDemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
    </div>
  );
}
